import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

const BACKEND = API.replace('/api', '');

export default function SponsorCarousel({ eventId = '', fallbackHeading = 'Our Corporate Members' }) {
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    const params = eventId ? `?event_id=${eventId}` : '';
    axios.get(`${API}/public/sponsors${params}`).then(r => setSponsors(r.data || [])).catch(() => {});
  }, [eventId]);

  // Group sponsors by tier
  const tiers = {};
  sponsors.forEach(s => {
    const tier = s.tier || 'corporate';
    if (!tiers[tier]) tiers[tier] = { heading: s.heading || '', logos: [] };
    tiers[tier].logos.push(s);
    if (s.heading) tiers[tier].heading = s.heading;
  });

  const tierOrder = ['platinum', 'gold', 'silver', 'corporate'];
  const defaultHeadings = {
    corporate: fallbackHeading,
    platinum: 'Platinum Sponsors',
    gold: 'Gold Sponsors',
    silver: 'Silver Sponsors',
  };

  const sortedTiers = tierOrder.filter(t => tiers[t] && tiers[t].logos.length > 0);
  if (sortedTiers.length === 0) return null;

  return (
    <div data-testid="sponsor-carousel">
      {sortedTiers.map(tier => {
        const { heading, logos } = tiers[tier];
        const displayHeading = heading || defaultHeadings[tier] || tier;
        // Duplicate logos 4x for seamless infinite scroll
        const scrollLogos = [...logos, ...logos, ...logos, ...logos];
        // Fast speed: ~3s per logo
        const duration = Math.max(8, logos.length * 3);

        return (
          <div key={tier} style={{ padding: '48px 0', overflow: 'hidden' }}>
            <h3 style={{
              textAlign: 'center', fontFamily: 'Poppins, sans-serif', fontSize: '22px',
              fontWeight: 800, color: '#0c3c60', marginBottom: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px'
            }}>
              <span style={{ width: '50px', height: '3px', background: 'linear-gradient(90deg, transparent, #1e7a4d)', borderRadius: '2px' }} />
              {displayHeading}
              <span style={{ width: '50px', height: '3px', background: 'linear-gradient(90deg, #1e7a4d, transparent)', borderRadius: '2px' }} />
            </h3>
            <div style={{ position: 'relative', overflow: 'hidden', maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)' }}>
              <div
                className="sponsor-scroll-track"
                style={{
                  display: 'flex', gap: '60px', alignItems: 'center',
                  animation: `sponsorScroll ${duration}s linear infinite`,
                  width: 'max-content',
                }}
              >
                {scrollLogos.map((s, i) => {
                  const logoSrc = s.logo_url?.startsWith('/api') ? `${BACKEND}${s.logo_url}` : s.logo_url;
                  return (
                    <a key={`${s.id}-${i}`} href={s.website_url || '#'} target={s.website_url ? '_blank' : '_self'} rel="noreferrer"
                      className="sponsor-logo-item"
                      title={s.name}
                    >
                      <img src={logoSrc} alt={s.name} className="sponsor-logo-img" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
      <style>{`
        @keyframes sponsorScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-25%); }
        }
        .sponsor-scroll-track:hover {
          animation-play-state: paused;
        }
        .sponsor-logo-item {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 200px;
          height: 120px;
          padding: 12px;
          background: white;
          border-radius: 12px;
          border: 1px solid #e8edf2;
          transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;
          text-decoration: none;
        }
        .sponsor-logo-item:hover {
          transform: scale(1.08);
          box-shadow: 0 8px 24px rgba(12,60,96,0.12);
          border-color: #1e7a4d;
        }
        .sponsor-logo-img {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
          transition: filter 0.3s;
          filter: grayscale(20%);
        }
        .sponsor-logo-item:hover .sponsor-logo-img {
          filter: grayscale(0%);
        }
        /* Mobile responsive */
        @media (max-width: 768px) {
          .sponsor-logo-item {
            width: 150px;
            height: 90px;
            padding: 10px;
          }
          .sponsor-scroll-track {
            gap: 32px !important;
          }
        }
        @media (max-width: 480px) {
          .sponsor-logo-item {
            width: 120px;
            height: 75px;
            padding: 8px;
          }
          .sponsor-scroll-track {
            gap: 24px !important;
          }
        }
      `}</style>
    </div>
  );
}
